import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, Box, Button, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import FilerobotImageEditor, { TABS } from 'react-filerobot-image-editor';
import { compressCanvasForUpload } from '../utils/imageProcessing.util';

interface PhotoEditorDialogProps {
  open: boolean;
  onClose: () => void;
  originalBlobName: string;
  fileName: string;
  onSave: (editedImageBlob: Blob, editedImageUrl: string) => Promise<void>;
  getImageAsBase64: (blobName: string) => Promise<string>;
}

const PhotoEditorDialog: React.FC<PhotoEditorDialogProps> = ({ open, onClose, originalBlobName, fileName, onSave, getImageAsBase64 }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [imageSource, setImageSource] = useState<string>('');
  const [isLoadingImage, setIsLoadingImage] = useState(false);

  // Fetch the base64 image when dialog opens
  useEffect(() => {
    if (open && originalBlobName) {
      const fetchImage = async () => {
        setIsLoadingImage(true);
        try {
          const base64Image = await getImageAsBase64(originalBlobName);
          setImageSource(base64Image);
        } catch (error) {
          console.error('Error fetching image:', error);
          toast.error('Failed to load image for editing');
        } finally {
          setIsLoadingImage(false);
        }
      };

      fetchImage();
    }
  }, [open, originalBlobName, getImageAsBase64]);

  const handleEditorSave = async (editedImageObject: any, designState: any) => {
    setIsSaving(true);
    try {
      // Convert the edited image to blob
      const canvas = editedImageObject.canvas || editedImageObject.imageCanvas;

      if (canvas) {
        const blob = await compressCanvasForUpload(canvas, 2800, 0.9);
        const editedImageUrl = URL.createObjectURL(blob);
        await onSave(blob, editedImageUrl);
        onClose();
      } else {
        toast.error('Failed to get edited image data');
      }
    } catch (error) {
      console.error('Error saving edited image:', error);
      toast.error('Failed to save edited image');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullScreen={true}>
      <DialogContent sx={{ p: 0, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            backgroundColor: 'white',
            borderBottom: '1px solid #e0e0e0',
            zIndex: 10,
          }}
        >
          <Typography variant='h6' component='h2'>
            Edit Photo: {fileName}
          </Typography>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
        </Box>

        {/* Editor Container */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {(isSaving || isLoadingImage) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: 30,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={60} />
                <Typography variant='body1' sx={{ mt: 2 }}>
                  {isLoadingImage ? 'Loading image...' : 'Saving edited image...'}
                </Typography>
              </Box>
            </Box>
          )}

          <div
            style={{
              width: '100%',
              height: '100%',
              minHeight: '500px',
            }}
          >
            {imageSource && (
              <FilerobotImageEditor
                source={imageSource}
                onSave={handleEditorSave}
                onClose={onClose}
                savingPixelRatio={1}
                previewPixelRatio={1}
                observePluginContainerSize={true}
                showCanvasOnly={false}
                useBackendTranslations={false}
                closeAfterSave={true}
                disableSaveIfNoChanges={true}
                defaultSavedImageName={fileName}
                defaultSavedImageType='jpeg'
                defaultSavedImageQuality={0.9}
                onBeforeSave={() => false}
                tabsIds={[TABS.ADJUST, TABS.FINETUNE, TABS.RESIZE]}
              />
            )}
          </div>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoEditorDialog;
